<div class="">
  <div class="max-content-width spaced-sections">
    @if (service.error()) {
      <app-base-alert
        type="error"
        [message]="service.error()!"
        [closeable]="true"
        (closed)="service.clearError()"
        data-testid="corpus-error-banner"
      ></app-base-alert>
    }

    @if (showNotMemberNotice()) {
      <div
        class="flex gap-3 rounded-r-lg border-l-4 border-indigo-500 bg-indigo-50 p-4 text-sm text-indigo-800 shadow-sm"
        data-testid="corpus-readonly-banner"
      >
        <span class="text-xl">ℹ️</span>
        <div>
          <p class="font-bold">Read-Only Mode</p>
          <p class="mt-0.5">
            You are viewing this corpus because you are not a researcher on this project. Only
            project researchers and the Principal Investigator (PI) can edit it.
          </p>
        </div>
      </div>
    }

    <!-- Provenance strip -->
    @if (displayedSummary(); as summary) {
      <app-provenance-strip
        [summary]="summary"
        [projectDetails]="projectDetails()"
        [protocol]="protocol()"
        [canAdvance]="
          runningImportJobIds().length === 0 && (displayedSummary()?.totalPapers ?? 0) > 0
        "
        [showAdvanceButton]="showAdvanceButton()"
        [suppressNeedsAttention]="runningImportJobIds().length > 0"
        (needsAttentionToggle)="onNeedsAttentionToggle()"
        (advance)="onAdvanceClick()"
      ></app-provenance-strip>
    } @else {
      <div
        class="card card-padding flex animate-pulse flex-col gap-3 md:flex-row md:items-center md:flex-wrap"
      >
        <div class="flex items-baseline gap-2">
          <div class="h-8 w-12 rounded bg-slate-200 skeleton-shimmer"></div>
          <div class="h-4 w-14 rounded bg-slate-200 skeleton-shimmer"></div>
        </div>
        <div class="flex flex-wrap items-center gap-2">
          <div class="h-6 w-24 rounded-full bg-slate-200 skeleton-shimmer"></div>
          <div class="h-6 w-20 rounded-full bg-slate-200 skeleton-shimmer"></div>
          <div class="h-6 w-28 rounded-full bg-slate-200 skeleton-shimmer"></div>
        </div>
        <div class="ml-auto h-9 w-44 rounded-lg bg-slate-200 skeleton-shimmer"></div>
      </div>
    }

    <!-- Import-job stream -->
    <div class="relative">
      <app-import-job-stream
        [jobs]="service.importJobs()"
        [activeJobId]="service.activeJobId()"
        [canUndo]="isCorpusEditable()"
        [currentUserId]="currentUserId()"
        [piUserId]="projectDetails()?.piUserId ?? ''"
        (jobClick)="onJobClick($event)"
        (jobDetail)="onJobDetail($event)"
        (titleChange)="onTitleChange($event)"
        (undoImport)="onUndoImport($event)"
      ></app-import-job-stream>
      @if (canAddPapers()) {
        <button type="button" (click)="openAddPapers()" class="btn-primary absolute top-3 right-5">
          + Add papers
        </button>
      }
    </div>

    <!-- Search reproducibility record (collapsed by default; same data, report shape) -->
    @if ((service.importJobs()?.items?.length ?? 0) > 0) {
      <section class="card card-padding">
        <div class="flex items-start gap-2">
          <button
            type="button"
            (click)="searchRecordCollapsed.set(!searchRecordCollapsed())"
            class="rounded-lg p-1 text-slate-400 hover:text-slate-600 hover:bg-slate-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-secondary/40 transition-colors mt-0.5 flex-shrink-0"
            [attr.aria-expanded]="!searchRecordCollapsed()"
            [attr.aria-label]="
              searchRecordCollapsed() ? 'Expand search record' : 'Collapse search record'
            "
          >
            <svg
              class="w-5 h-5 transition-transform duration-200"
              [class.rotate-180]="!searchRecordCollapsed()"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </button>
          <div>
            <h2 class="section-title">
              Search record
              <span class="text-base font-normal text-gray-500">
                ({{ service.importJobs()?.items?.length ?? 0 }})
              </span>
            </h2>
          </div>
        </div>
        @if (!searchRecordCollapsed()) {
          <div class="mt-3">
            <app-search-record [jobs]="service.importJobs()?.items ?? []"></app-search-record>
          </div>
        }
      </section>
    }

    <!-- Active-job context bar -->
    @if (activeJob(); as j) {
      <app-job-context-bar [job]="j" (clear)="onJobClick(null)"></app-job-context-bar>
    }

    <!-- Paper list (Phase 4) -->
    <app-paper-list
      [papers]="service.papers()"
      [loading]="service.loading()"
      [filter]="service.currentFilter()"
      [pageReq]="service.currentPage()"
      [projectState]="service.summary()?.state ?? 'INGESTION'"
      [canEdit]="isProjectMember()"
      [runningImportJobIds]="runningImportJobIds()"
      (filterChange)="service.setFilter($event)"
      (pageChange)="service.setPage($event)"
      (editPaper)="editingPaper.set($event)"
      (attachPdf)="attachingPaper.set($event)"
      (viewDetailsPaper)="viewingPaper.set($event)"
    ></app-paper-list>
  </div>

  <!-- Phase 5 + 6 dialogs (rendered at the bottom; conditional on their inputs) -->
  <app-add-papers-dialog
    [open]="addPapersOpen()"
    (openChange)="onAddPapersOpenChange($event)"
  ></app-add-papers-dialog>

  <app-edit-metadata-dialog
    [paper]="editingPaper()"
    (openChange)="onEditingPaperOpenChange($event)"
  ></app-edit-metadata-dialog>

  <app-edit-metadata-dialog
    [paper]="viewingPaper()"
    [readonly]="true"
    (openChange)="viewingPaper.set(null)"
  ></app-edit-metadata-dialog>

  <app-attach-pdf-dialog
    [paper]="attachingPaper()"
    (openChange)="onAttachingPaperOpenChange($event)"
  ></app-attach-pdf-dialog>

  <app-job-detail-dialog
    [job]="detailJob()"
    (closed)="detailJob.set(null)"
    (editPaper)="onEditPaperFromJob($event)"
  ></app-job-detail-dialog>

  <app-confirm-modal
    [open]="advanceConfirmOpen()"
    [title]="advanceConfirmTitle()"
    [message]="advanceConfirmMessage()"
    [messageHtml]="advanceConfirmMessageHtml()"
    [confirmText]="advanceConfirmButtonText()"
    [confirmVariant]="isForceAdvance() ? 'danger' : 'primary'"
    [loading]="advancing()"
    (openChange)="onAdvanceConfirmOpenChange($event)"
    (confirmed)="onAdvanceConfirmed()"
  ></app-confirm-modal>

  <app-confirm-modal
    [open]="undoConfirmOpen()"
    title="Undo this import?"
    message="This will remove all papers added by this import from the project corpus."
    confirmText="Undo"
    confirmVariant="danger"
    [loading]="undoing()"
    (openChange)="onUndoConfirmOpenChange($event)"
    (confirmed)="onUndoConfirmed()"
  ></app-confirm-modal>
</div>
